import { TooltipContentProps } from "@radix-ui/react-tooltip";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ENTITY_SWITCHER_CLICKED } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useEffect } from "react";
import { useGetGroupDataQuery } from "store/apis/group";
import { entitiesSortedByCountry } from "utils/entitiesSortedByCountry";
import BareEntityCombobox from "./BareEntityCombobox";

type EntitySelectorProps = {
  showOnlyUSEntities?: boolean;
  handleEntitySelect?: (entityId: string) => void;
  toolTipPlacement?: TooltipContentProps["side"];
  right?: number;
  disableNonUsEntities?: boolean;
  showGoToEntity?: boolean;
  size?: "small" | "regular" | "large";
  left?: number;
  backgroundColor?: string;
};

export const EntitySelector = ({
  showOnlyUSEntities = false,
  toolTipPlacement = "left",
  handleEntitySelect,
  right = 0,
  disableNonUsEntities = false,
  showGoToEntity = false,
  size = "regular",
  left,
  backgroundColor,
}: EntitySelectorProps) => {
  const { update, updateMultiple } = useUpdateQuery();
  const query = useQuery();
  const entityFromUrl = query.get("entity");
  let selectedEntityId = entityFromUrl;
  const groupId = query.get("company") || "";
  const { entities, uuid: currentGroupId } = useCurrentGroupContext();
  const { sortedEntities, usEntities } = entitiesSortedByCountry(entities);
  const displayedEntities = showOnlyUSEntities ? usEntities : sortedEntities;
  selectedEntityId ??= displayedEntities?.[0]?.uuid;
  const { trackEvent } = useAnalytics();
  const { isCustomer } = useRoleBasedView();

  const { isFetching } = useGetGroupDataQuery(
    { groupId },
    { skip: !groupId || isCustomer }
  );

  const {
    name: selectedEntityName,
    country: selectedEntityCountry,
    country_code: selectedEntityCountryCode,
    uuid: currentSelectedEntityId,
  } = displayedEntities.find(({ uuid }) => uuid === selectedEntityId) ||
  displayedEntities?.[0] ||
  {};

  const isGroupLoaded =
    Boolean(currentGroupId) && (!groupId || currentGroupId === groupId);
  const isUrlEntityOutsideGroup =
    Boolean(entityFromUrl) &&
    isGroupLoaded &&
    !displayedEntities.some(({ uuid }) => uuid === entityFromUrl);

  useEffect(() => {
    if (
      currentSelectedEntityId &&
      (!entityFromUrl || isUrlEntityOutsideGroup)
    ) {
      update({
        query: "entity",
        value: currentSelectedEntityId,
      });
    }
  }, [currentSelectedEntityId, entityFromUrl, isUrlEntityOutsideGroup, update]);

  const onEntitySelect = ({ uuid }: { uuid: string }) => {
    trackEvent(ENTITY_SWITCHER_CLICKED, {
      url: window.location.href,
    });
    if (handleEntitySelect) {
      handleEntitySelect(uuid);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);

    const updates: { query: string; value: string | null }[] = Array.from(
      searchParams.keys()
    ).map((key) => {
      return { query: key, value: null };
    });

    updates.push({ query: "company", value: searchParams.get("company") });
    updates.push({ query: "entity", value: uuid });

    updateMultiple(updates);
  };

  const isEntitySelected =
    selectedEntityId &&
    selectedEntityCountry &&
    selectedEntityName &&
    selectedEntityCountryCode;
  return (
    <ConditionalToolTip
      condition={isEntitySelected && selectedEntityName}
      side={toolTipPlacement}
    >
      <span className="t-block t-w-full">
        <BareEntityCombobox
          size={size}
          isDisabled={!isEntitySelected || isFetching}
          isLoading={!isEntitySelected || isFetching}
          onChange={(value) => {
            onEntitySelect({ uuid: value });
          }}
          right={right}
          left={left}
          value={currentSelectedEntityId}
          showOnlyUSEntities={showOnlyUSEntities}
          disableNonUsEntities={disableNonUsEntities}
          isEntitySelected={isEntitySelected}
          showGoToEntity={showGoToEntity}
          controlBackground={backgroundColor}
        />
      </span>
    </ConditionalToolTip>
  );
};
